import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Video, Upload, FileText, Trash2, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../ui/ConfirmDialog';
import { toast } from '../ui/Toaster';

interface ModuleFile {
  id?: string;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size?: number;
  file?: File;
}

interface Module {
  id?: string;
  name: string;
  description: string;
  objective: string;
  start_date: string;
  end_date: string;
  zoom_link: string;
  order_index: number;
}

interface ModuleFormProps {
  module: Module;
  index: number;
  onUpdate: (index: number, field: keyof Module, value: string | number) => void;
  onRemove: (index: number) => void;
}

export function ModuleForm({ module, index, onUpdate, onRemove }: ModuleFormProps) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [files, setFiles] = useState<ModuleFile[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);

  useEffect(() => {
    if (module.id) {
      loadModuleFiles();
    }
  }, [module.id]);

  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Ensure fileName has proper extension
      let downloadName = fileName;
      if (!downloadName.includes('.')) {
        // Extract extension from URL if fileName doesn't have one
        const urlPath = fileUrl.split('?')[0];
        const urlExt = urlPath.split('.').pop()?.toLowerCase();
        if (urlExt && ['png', 'jpg', 'jpeg', 'pdf', 'docx', 'xlsx', 'pptx', 'zip', 'rar', 'rvt', 'rfa', 'rte', 'dwg'].includes(urlExt)) {
          downloadName = `${fileName}.${urlExt}`;
        }
      }

      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Error al descargar el archivo');
    }
  };

  const loadModuleFiles = async () => {
    if (!module.id) return;

    setLoadingFiles(true);
    try {
      const { data, error } = await supabase
        .from('module_files')
        .select('*')
        .eq('module_id', module.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFiles(data || []);
    } catch (error) {
      console.error('Error loading module files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    for (const file of selectedFiles) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`“${file.name}” supera los 50MB`);
        continue;
      }

      const fileType = getFileType(file.type);
      const newFile: ModuleFile = {
        file_name: file.name,
        file_url: '',
        file_type: fileType,
        file_size: file.size,
        file: file,
      };

      if (module.id) {
        await uploadFile(newFile);
      } else {
        setFiles(prev => [...prev, newFile]);
      }
    }
  };

  const getFileType = (mimeType: string): string => {
    if (mimeType.includes('pdf')) return 'pdf';
    if (mimeType.includes('video')) return 'video';
    if (mimeType.includes('image')) return 'image';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
    return 'other';
  };

  const uploadFile = async (fileData: ModuleFile) => {
    if (!fileData.file || !module.id) return;

    setUploadingFile(true);
    try {
      const fileExt = fileData.file_name.split('.').pop();
      const fileName = `module-${module.id}-${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `modules/${fileName}`;

      const { data: bucketData, error: bucketError } = await supabase.storage
        .from('module-files')
        .upload(filePath, fileData.file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (bucketError) {
        console.error('Upload error:', bucketError);
        throw new Error(`Error al subir archivo: ${bucketError.message}`);
      }

      const { data: urlData } = supabase.storage
        .from('module-files')
        .getPublicUrl(filePath);

      const { error: insertError } = await supabase
        .from('module_files')
        .insert([{
          module_id: module.id,
          file_name: fileData.file_name,
          file_url: urlData.publicUrl,
          file_type: fileData.file_type,
          file_size: fileData.file_size,
          uploaded_by: user?.id,
        }]);

      if (insertError) {
        console.error('Insert error:', insertError);
        throw new Error(`Error al guardar archivo: ${insertError.message}`);
      }

      await loadModuleFiles();
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Error al subir el archivo. Intenta nuevamente.');
    } finally {
      setUploadingFile(false);
    }
  };

  const deleteFile = async (fileId: string) => {
    const ok = await confirm({
      title: 'Eliminar archivo',
      description: '¿Seguro que deseas eliminar este archivo del módulo?',
      confirmText: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      const { error } = await supabase
        .from('module_files')
        .delete()
        .eq('id', fileId);

      if (error) throw error;
      await loadModuleFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Error al eliminar el archivo');
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDateTime = (dateStr: string): string => {
    if (!dateStr) return '';
    return dateStr.slice(0, 16);
  };

  const handleDateChange = (field: 'start_date' | 'end_date', value: string) => {
    if (!value) {
      onUpdate(index, field, '');
      return;
    }
    onUpdate(index, field, value + ':00');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
      <div className="md:col-span-2">
        <input
          type="text"
          placeholder="Nombre del módulo"
          value={module.name}
          onChange={(e) => onUpdate(index, 'name', e.target.value)}
          className="w-full px-3 py-2 glass-surface border border-white/[0.1] rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-cubus-lime"
        />
      </div>

      <div className="md:col-span-2">
        <textarea
          placeholder="Descripción"
          rows={3}
          value={module.description}
          onChange={(e) => onUpdate(index, 'description', e.target.value)}
          className="w-full px-3 py-2 glass-surface border border-white/[0.1] rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-cubus-lime"
        />
      </div>

      <div className="md:col-span-2">
        <input
          type="text"
          placeholder="Objetivo del módulo"
          value={module.objective}
          onChange={(e) => onUpdate(index, 'objective', e.target.value)}
          className="w-full px-3 py-2 glass-surface border border-white/[0.1] rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-cubus-lime"
        />
      </div>

      <div>
        <label className="block text-xs text-muted mb-1">Fecha y hora de inicio</label>
        <input
          type="datetime-local"
          value={formatDateTime(module.start_date)}
          onChange={(e) => handleDateChange('start_date', e.target.value)}
          className="w-full px-3 py-2 glass-surface border border-white/[0.1] rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-cubus-lime"
        />
      </div>

      <div>
        <label className="block text-xs text-muted mb-1">Fecha y hora de fin</label>
        <input
          type="datetime-local"
          value={formatDateTime(module.end_date)}
          onChange={(e) => handleDateChange('end_date', e.target.value)}
          className="w-full px-3 py-2 glass-surface border border-white/[0.1] rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-cubus-lime"
        />
      </div>

      <div className="md:col-span-2 flex gap-2">
        <Video className="w-5 h-5 text-muted mt-2" />
        <input
          type="url"
          placeholder="Link de Zoom (https://zoom.us/...)"
          value={module.zoom_link}
          onChange={(e) => onUpdate(index, 'zoom_link', e.target.value)}
          className="flex-1 px-3 py-2 glass-surface border border-white/[0.1] rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-cubus-lime"
        />
      </div>

      <div className="md:col-span-2 border-t border-white/[0.1] pt-3">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-white/70">Archivos del Módulo</label>
          <label className="cursor-pointer">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-cubus-lime/10 hover:bg-cubus-lime/20 border border-cubus-lime/30 rounded-lg text-cubus-lime text-xs transition-all">
              <Upload className="w-3.5 h-3.5" />
              {uploadingFile ? 'Subiendo...' : 'Agregar Archivo'}
            </div>
            <input
              type="file"
              multiple
              accept=".png,.pdf,.rvt,.rfa,.rte,.jpeg,.jpg,.dwg,.docx,.xlsx,.pptx,.zip,.rar"
              onChange={handleFileSelect}
              disabled={uploadingFile || !module.id}
              className="hidden"
            />
          </label>
        </div>

        {!module.id && (
          <p className="text-xs text-subtle mb-2">
            Guarda el curso primero para poder subir archivos
          </p>
        )}

        {files.length > 0 && (
          <div className="space-y-2">
            {files.map((file, idx) => (
              <div
                key={file.id || idx}
                className="flex items-center justify-between p-2 glass-deep rounded border border-white/[0.08]"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <FileText className="w-4 h-4 text-cubus-lime flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{file.file_name}</p>
                    <p className="text-xs text-subtle">{formatFileSize(file.file_size)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {file.file_url && (
                    <button
                      type="button"
                      onClick={() => handleDownload(file.file_url, file.file_name)}
                      className="p-1.5 text-cubus-lime hover:bg-cubus-lime/10 rounded transition-all"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {file.id && (
                    <button
                      type="button"
                      onClick={() => deleteFile(file.id!)}
                      className="p-1.5 text-red-400 hover:bg-red-500/10 rounded transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
